import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle2,
  Circle,
  User,
  Clock,
  FileText,
  X,
  Sparkles,
  ArrowRight,
  RotateCcw,
} from "lucide-react";

interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  icon: typeof User;
  actionLabel: string;
  check: () => Promise<boolean>;
}

interface ProviderOnboardingChecklistProps {
  onNavigate?: (section: "profile" | "availability" | "patients") => void;
}

const ProviderOnboardingChecklist = ({ onNavigate }: ProviderOnboardingChecklistProps) => {
  const { user } = useAuth();
  const [completedSteps, setCompletedSteps] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const items: ChecklistItem[] = [
    {
      id: "profile",
      label: "Complete your profile",
      description: "Add your name and contact details in your Provider Profile",
      icon: User,
      actionLabel: "Go to Settings",
      check: async () => {
        if (!user) return false;
        const { data } = await supabase
          .from("profiles")
          .select("first_name, last_name, phone")
          .eq("user_id", user.id)
          .single();
        return !!(data?.first_name && data?.last_name);
      },
    },
    {
      id: "availability",
      label: "Set your availability",
      description: "Add at least one availability slot for appointments",
      icon: Clock,
      actionLabel: "Set Availability",
      check: async () => {
        if (!user) return false;
        const { data } = await supabase
          .from("provider_availability")
          .select("id")
          .eq("provider_id", user.id)
          .limit(1);
        return !!(data && data.length > 0);
      },
    },
    {
      id: "review_assessment",
      label: "Review your first patient",
      description: "Open a patient assessment and add clinical notes",
      icon: FileText,
      actionLabel: "View Patients",
      check: async () => {
        if (!user) return false;
        const { data } = await supabase
          .from("provider_notes")
          .select("id")
          .eq("provider_id", user.id)
          .limit(1);
        return !!(data && data.length > 0);
      },
    },
  ];

  const runChecks = useCallback(async () => {
    const results: Record<string, boolean> = {};
    await Promise.all(
      items.map(async (item) => {
        results[item.id] = await item.check();
      })
    );
    setCompletedSteps(results);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const dismissKey = `provider_onboarding_dismissed_${user.id}`;
    if (localStorage.getItem(dismissKey) === "true") {
      setDismissed(true);
      // Still run checks so we know if all complete
      runChecks();
      return;
    }

    runChecks();
  }, [user, runChecks]);

  const handleDismiss = () => {
    if (user) {
      localStorage.setItem(`provider_onboarding_dismissed_${user.id}`, "true");
    }
    setDismissed(true);
  };

  const handleReshow = () => {
    if (user) {
      localStorage.removeItem(`provider_onboarding_dismissed_${user.id}`);
    }
    setDismissed(false);
  };

  const handleItemClick = (itemId: string) => {
    if (!onNavigate) return;
    switch (itemId) {
      case "profile":
        onNavigate("profile");
        break;
      case "availability":
        onNavigate("availability");
        break;
      case "review_assessment":
        onNavigate("patients");
        break;
    }
  };

  const completedCount = Object.values(completedSteps).filter(Boolean).length;
  const totalCount = items.length;
  const allComplete = completedCount === totalCount;
  const progressPercent = Math.round((completedCount / totalCount) * 100);

  if (loading) return null;
  if (allComplete) return null;

  // Show a small "re-show" banner when dismissed but not complete
  if (dismissed) {
    return (
      <div className="mb-6 flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3">
        <p className="text-sm text-muted-foreground">
          <Sparkles size={14} className="inline mr-1.5 text-primary" />
          Onboarding: {completedCount}/{totalCount} steps complete
        </p>
        <Button variant="ghost" size="sm" onClick={handleReshow} className="gap-1.5 text-xs">
          <RotateCcw size={12} /> Show checklist
        </Button>
      </div>
    );
  }

  return (
    <Card className="border-primary/20 bg-primary/5 mb-8">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Sparkles size={18} className="text-primary" />
            </div>
            <div>
              <h3 className="font-display text-lg text-foreground">
                Welcome aboard!
              </h3>
              <p className="text-sm text-muted-foreground">
                Complete these steps to get started as a provider
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            onClick={handleDismiss}
          >
            <X size={16} />
          </Button>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="text-muted-foreground">
              {completedCount} of {totalCount} complete
            </span>
            <span className="font-medium text-primary">{progressPercent}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        <div className="space-y-3">
          {items.map((item) => {
            const done = completedSteps[item.id];
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => !done && handleItemClick(item.id)}
                disabled={done}
                className={`w-full flex items-center gap-3 rounded-lg px-4 py-3 transition-colors text-left ${
                  done
                    ? "bg-primary/10 opacity-70 cursor-default"
                    : "bg-card border border-border hover:border-primary/30 cursor-pointer"
                }`}
              >
                {done ? (
                  <CheckCircle2 size={20} className="text-primary shrink-0" />
                ) : (
                  <Circle size={20} className="text-muted-foreground shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm font-medium ${
                      done ? "line-through text-muted-foreground" : "text-foreground"
                    }`}
                  >
                    {item.label}
                  </p>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                </div>
                {!done ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-primary shrink-0">
                    {item.actionLabel} <ArrowRight size={12} />
                  </span>
                ) : (
                  <item.icon
                    size={16}
                    className="text-primary/50 shrink-0"
                  />
                )}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProviderOnboardingChecklist;
